import { KeychainAccess, SecureStorage } from '@aparajita/capacitor-secure-storage';

import { isStoreBuild } from './platform';

/**
 * Хранилище сессии: в приложении — защищённое системное, в браузере — обычное.
 *
 * ЗАЧЕМ. Токены входа лежали в `localStorage` веб-вида. Отсюда два пути утечки:
 * успешный XSS читал их из страницы, а на Android содержимое приложения уезжало
 * в облачную копию Google. Здесь используется хранилище секретов платформы —
 * Keychain на iOS и Keystore на Android: оно шифруется системой, веб-странице
 * недоступно, и в него нельзя заглянуть, просто добравшись до файлов приложения.
 *
 * ПОЧЕМУ НЕ ОБЫЧНЫЕ НАСТРОЙКИ ПРИЛОЖЕНИЯ. Первая версия этого модуля брала
 * `@capacitor/preferences` — это `UserDefaults` и `SharedPreferences`. Они
 * действительно недоступны веб-странице, но **не шифруются**: файл настроек
 * читается как обычный. Для токенов входа этого мало.
 *
 * УРОВЕНЬ ДОСТУПА — `whenUnlockedThisDeviceOnly`:
 *   - читается только когда устройство разблокировано;
 *   - **не попадает в резервные копии** и не переносится на новый телефон.
 * Второе важно: оно закрывает на iOS ту же дыру, что `allowBackup="false"`
 * закрывает на Android. Цена — при смене телефона нужно войти заново, что для
 * учебного приложения нормально: все данные и так на сервере.
 *
 * ⚠️ ЦЕНА РЕШЕНИЯ — АСИНХРОННОСТЬ. `localStorage` читается мгновенно, защищённое
 * хранилище — нет. Значит в первый миг после запуска токена «ещё нет», и всё,
 * что судит о входе по его наличию, обязано дождаться восстановления. Иначе
 * защита маршрутов увидит пустоту и выбросит вошедшего на экран входа при
 * каждом запуске. Ожидание сделано в `RoleGate` через `hasHydrated`.
 *
 * ПЕРЕНОС СТАРОЙ СЕССИИ. У тех, кто уже вошёл, токены лежат в `localStorage`.
 * При первом запуске новой сборки они переносятся, иначе обновление приложения
 * разлогинило бы всех разом.
 */

let configured = false;

async function ensureConfigured(): Promise<void> {
  if (configured) return;
  configured = true;
  try {
    // Не синхронизировать между устройствами через iCloud — это тот же перенос
    // ключа наружу, от которого мы уходим.
    await SecureStorage.setSynchronize(false);
    await SecureStorage.setDefaultKeychainAccess(KeychainAccess.whenUnlockedThisDeviceOnly);
  } catch {
    /* плагин недоступен — работаем по запасному пути ниже */
  }
}

/**
 * Состояние сессии без учётки и без обоих ключей — то, что пишет `logout` в `stores/auth.ts`.
 *
 * ⚠️ ВЫХОД УДАЛЯЕТ ЗАПИСЬ, А НЕ ПИШЕТ ПУСТУЮ (13.10.2026, сборка 1.1.2). Прежде после «Выйти» в Keychain оставалась
 * запись `{"state":{"user":null,…}}`: секрета в ней нет, но она переживает выход и переустановку приложения
 * (Keychain iOS при удалении приложения не чистится) — сама запись говорит, что на этом телефоне входили.
 *
 * Решается здесь, в адаптере, а не отдельным удалением в `logout`: запись делает сам `persist` после `set`, и
 * удаление рядом с ним гонялось бы с этой записью — пустая легла бы поверх удалённой. Так же покрыт вынужденный
 * выход (протухший ключ продления), который идёт через тот же `logout`. Неразборчивое значение — пишем как есть.
 */
function isEmptySession(value: string): boolean {
  try {
    const s = (JSON.parse(value) as { state?: Record<string, unknown> } | null)?.state;
    return !!s && s.user == null && s.accessToken == null && s.refreshToken == null;
  } catch {
    return false;
  }
}

export const sessionStorageAdapter = {
  async getItem(name: string): Promise<string | null> {
    if (!isStoreBuild()) return localStorage.getItem(name);
    await ensureConfigured();
    try {
      const value = await SecureStorage.getItem(name);
      if (value != null) return value;
      // Первый запуск после обновления: забираем то, что осталось от старой
      // сборки, и сразу перекладываем в защищённое хранилище.
      const legacy = localStorage.getItem(name);
      if (legacy != null) {
        await SecureStorage.setItem(name, legacy);
        localStorage.removeItem(name);
        return legacy;
      }
      return null;
    } catch {
      // Плагин недоступен — лучше пустить по старому пути, чем разлогинить.
      return localStorage.getItem(name);
    }
  },

  async setItem(name: string, value: string): Promise<void> {
    // Выход — запись удаляется целиком, а не переписывается пустой (решение техлида 13.10.2026, сборка 1.1.2).
    if (isEmptySession(value)) {
      await sessionStorageAdapter.removeItem(name);
      return;
    }
    if (!isStoreBuild()) {
      localStorage.setItem(name, value);
      return;
    }
    await ensureConfigured();
    try {
      await SecureStorage.setItem(name, value);
    } catch {
      localStorage.setItem(name, value);
    }
  },

  async removeItem(name: string): Promise<void> {
    localStorage.removeItem(name); // подчищаем и старое место
    if (!isStoreBuild()) return;
    try {
      await SecureStorage.removeItem(name);
    } catch {
      /* нечего удалять — и не надо */
    }
  },
};

/** Имя записи сессии — на случай, если понадобится снаружи. */
export const SESSION_KEY = 'onebilim-auth';
